/**
 * TikTok pixel: loads only when a pixel id is configured (wrangler var TIKTOK_PIXEL_ID), so nothing
 * runs until the TikTok Ads account exists. Key steps also go server-side through the Events API with
 * the same event id (worker/index.ts), so TikTok counts each conversion once.
 */
type Ttq = { load: (id: string) => void; page: () => void; track: (event: string, props?: Record<string, unknown>, opts?: { event_id?: string }) => void; identify: (data: Record<string, string>) => void };
declare global { interface Window { ttq?: Ttq & unknown[]; TiktokAnalyticsObject?: string } }

let loaded = false;

export function loadTikTokPixel(pixelId: string | null | undefined): void {
  if (loaded || !pixelId || !/^[A-Z0-9]{10,30}$/i.test(pixelId)) return;
  loaded = true;
  // TikTok's standard loader, written out (no eval), queueing calls until the script arrives.
  const w = window as unknown as Record<string, unknown> & Window;
  w.TiktokAnalyticsObject = "ttq";
  const ttq = (w.ttq = (w.ttq || []) as unknown as Ttq & unknown[] & Record<string, unknown>) as unknown as Record<string, unknown> & unknown[];
  const methods = ["page", "track", "identify", "instances", "debug", "on", "off", "once", "ready", "alias", "group", "enableCookie", "disableCookie", "holdConsent", "revokeConsent", "grantConsent"];
  for (const m of methods) ttq[m] = (...args: unknown[]) => { (ttq as unknown[]).push([m, ...args]); };
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://analytics.tiktok.com/i18n/pixel/events.js?sdkid=${encodeURIComponent(pixelId)}&lib=ttq`;
  document.head.appendChild(script);
  (ttq as unknown as Ttq).page();
}

export function tiktokTrack(event: string, eventId: string, props: Record<string, unknown> = {}): void {
  if (!loaded || !window.ttq) return;
  try { window.ttq.track(event, props, { event_id: eventId }); } catch { /* never break the page for ads */ }
}

export async function tiktokIdentify(email: string, externalId: string): Promise<void> {
  if (!loaded || !window.ttq) return;
  const hash = async (v: string) => Array.from(new Uint8Array(await crypto.subtle.digest("SHA-256", new TextEncoder().encode(v.trim().toLowerCase()))), (b) => b.toString(16).padStart(2, "0")).join("");
  try { window.ttq.identify({ email: await hash(email), external_id: await hash(externalId) }); } catch { /* ignore */ }
}
